import React, { useCallback, useEffect, useState } from 'react';
import { MIcon } from '../../../lib/micon';
import { apiFetch, zeigeApiFehler } from '../../../lib/api';
import { toast } from '../../../lib/toast';
import { confirm } from '../../../lib/confirm';
import type { ElternKontakt, Gespraech, TerminOffen } from './profil-api';
import { wtTm } from './format';
import { ANLAESSE, ARTEN, ROLLE_TEXT } from './GespraechSheet';

const ART_ICON: Record<string, string> = { elterngespraech: 'family_restroom', schuelergespraech: 'person', telefonat: 'call', sonstiges: 'forum' };
const ART_TEXT: Record<string, string> = { ...Object.fromEntries(ARTEN), sonstiges: 'Gespräch' };
const ANLASS_TEXT: Record<string, string> = Object.fromEntries(ANLAESSE);
const schuljahrVon = (iso: string) => { const j = Number(iso.slice(0, 4)), m = Number(iso.slice(5, 7)); const s = m >= 8 ? j : j - 1; return `${s}/${String(s + 1).slice(2)}`; };

/** Welle P1c: Tab Gespräche — je Schuljahr, Vereinbarungen mit Status, Nachfassen; fremde nur lesen */
export function GespraecheTab({ schuelerId, eltern, neuLadenKey, ohneKopf = false, termineOffen = [], onAusTermin, onBearbeiten, onGeaendert }: {
  schuelerId: number; eltern: ElternKontakt[]; neuLadenKey: number;
  ohneKopf?: boolean; // Welle P1d: am Handy steht der Titel im Seitenkopf
  /** Welle B2: bestätigte Eltern-Termine ohne Gespräch → „Gespräch dokumentieren“ */
  termineOffen?: TerminOffen[]; onAusTermin?: (terminId: number) => void;
  onBearbeiten: (g: Gespraech) => void; onGeaendert: () => void;
}) {
  const [liste, setListe] = useState<Gespraech[] | null>(null);
  const [fehler, setFehler] = useState(false);
  const laden = useCallback(() => {
    setFehler(false);
    apiFetch<Gespraech[]>(`/api/gespraeche?schueler_id=${schuelerId}`).then(setListe).catch(() => setFehler(true));
  }, [schuelerId]);
  useEffect(() => { laden(); }, [laden, neuLadenKey]);

  async function vereinbarungUmschalten(g: Gespraech, vid: number, status: 'offen' | 'erledigt') {
    try {
      await apiFetch(`/api/gespraeche/${g.id}/vereinbarungen/${vid}`, { method: 'PATCH', body: JSON.stringify({ status }) });
      laden(); onGeaendert();
      toast(status === 'erledigt' ? 'Vereinbarung erledigt' : 'Vereinbarung wieder offen', 'success', status === 'erledigt' ? {
        dauerMs: 5000, aktion: { label: 'Rückgängig', onClick: () => vereinbarungUmschalten(g, vid, 'offen') } } : {});
    } catch (err) { zeigeApiFehler(err, 'Ändern fehlgeschlagen'); }
  }
  async function loeschen(g: Gespraech) {
    if (!(await confirm(`${ART_TEXT[g.typ] ?? 'Gespräch'} vom ${wtTm(g.datum)} löschen? Vereinbarungen und die Nachfassen-Aufgabe werden mit gelöscht.`, { title: 'Gespräch löschen?', danger: true, confirmLabel: 'Löschen' }))) return;
    try { await apiFetch(`/api/gespraeche/${g.id}`, { method: 'DELETE' }); toast('Gespräch gelöscht', 'success'); laden(); onGeaendert(); }
    catch (err) { zeigeApiFehler(err, 'Löschen fehlgeschlagen'); }
  }

  const gruppen = new Map<string, Gespraech[]>();
  for (const g of liste ?? []) { const k = schuljahrVon(g.datum); gruppen.set(k, [...(gruppen.get(k) ?? []), g]); }
  const elternName = (t: { eltern_id?: number; name: string }) => {
    const e = t.eltern_id ? eltern.find(x => x.id === t.eltern_id) : null;
    return e ? `${t.name} (${ROLLE_TEXT[e.rolle]})` : t.name;
  };

  return (
    <section className="ld-pu-karte ld-gt">
      {/* Welle P1d: angelegt wird an fester Stelle (PC: neben den Reitern, Handy: Leiste unten) */}
      {!ohneKopf && <header className="ld-gt__kopf"><h3><MIcon name="forum" size={19} />Gespräche</h3></header>}
      {termineOffen.length > 0 && onAusTermin && (
        <ul className="ld-gt__termine" aria-label="Eltern-Termine ohne Gespräch">
          {termineOffen.map(t => (
            <li key={t.termin_id} className="ld-gt__termin">
              <MIcon name={t.typ === 'telefonisch' ? 'call' : 'event'} size={18} />
              <span><b>{t.typ === 'telefonisch' ? 'Telefontermin' : 'Elterngespräch'}</b> {wtTm(t.datum)}{t.zeit ? `, ${t.zeit}` : ''}</span>
              <button type="button" className="ld-pu-sa__knopf" onClick={() => onAusTermin(t.termin_id)}>Gespräch dokumentieren</button>
            </li>
          ))}
        </ul>
      )}
      {fehler && !liste ? <div className="ld-pu-fehler" role="alert"><span>Konnte nicht geladen werden.</span><button type="button" onClick={laden}><MIcon name="refresh" size={15} />Erneut versuchen</button></div>
        : !liste ? <div className="ld-pu-laedt"><span /><span /></div>
        : liste.length === 0 ? <div className="ld-pu-leer">Noch kein Gespräch dokumentiert.</div>
        : Array.from(gruppen.entries()).map(([sj, gs]) => (
          <div key={sj} className="ld-gt__sj">
            <h4 className="ld-mt__unter">Schuljahr {sj}</h4>
            {gs.map(g => (
              <article key={g.id} className="ld-gt__g">
                <div className="ld-gt__g-kopf">
                  <span className={`ld-gt__icon ld-gt__icon--${g.typ}`}><MIcon name={ART_ICON[g.typ] ?? 'forum'} size={19} /></span>
                  <div className="ld-gt__g-titel">
                    <b>{ART_TEXT[g.typ] ?? 'Gespräch'}{g.termin_id ? <span className="ld-gt__aus-termin">aus Termin</span> : null}{g.vertraulich === 1 && <span className="ld-gt__vertraulich"><MIcon name="lock" size={13} />vertraulich</span>}</b>
                    <span>{wtTm(g.datum)}{g.datum.length > 10 && g.datum.slice(11, 16) !== '00:00' ? ` · ${g.datum.slice(11, 16)}` : ''}{g.eigen === false && g.von ? ` · Von ${g.von}` : ''}</span>
                  </div>
                  {g.eigen !== false && (
                    <div className="ld-gt__knoepfe">
                      <button type="button" className="ld-pk-icon ld-pk-icon--klein" aria-label="Gespräch bearbeiten" onClick={() => onBearbeiten(g)}><MIcon name="edit" size={16} /></button>
                      <button type="button" className="ld-pk-icon ld-pk-icon--klein" aria-label="Gespräch löschen" onClick={() => loeschen(g)}><MIcon name="delete" size={16} /></button>
                    </div>
                  )}
                </div>
                {(g.teilnehmer_liste.length > 0 || g.anlaesse.length > 0) && (
                  <div className="ld-gt__chips">
                    {g.teilnehmer_liste.map((t, i) => <span key={`t${i}`} className="ld-gt__chip ld-gt__chip--person">{elternName(t)}</span>)}
                    {g.anlaesse.map(a => <span key={a} className="ld-gt__chip">{ANLASS_TEXT[a] ?? a}</span>)}
                  </div>
                )}
                {g.anlass && !g.anlaesse.length && <p className="ld-gt__anlass">Anlass: {g.anlass}</p>}
                {g.inhalt && <p className="ld-gt__inhalt">{g.inhalt}</p>}
                {(g.vereinbarungen.length > 0 || g.nachfassen_am) && (
                  <div className="ld-gt__vbs">
                    {g.vereinbarungen.map(v => (
                      <div key={v.id} className={`ld-gt__vb${v.status === 'erledigt' ? ' ld-gt__vb--erledigt' : ''}`}>
                        <span><b>Vereinbarung:</b> {v.text}{v.wer ? ` · ${v.wer}` : ''}{v.bis_datum ? ` · bis ${wtTm(v.bis_datum)}` : ''}</span>
                        {g.eigen !== false
                          ? <button type="button" className={`ld-gt__status ld-gt__status--${v.status}`} aria-label={`Vereinbarung als ${v.status === 'offen' ? 'erledigt' : 'offen'} markieren`}
                              onClick={() => vereinbarungUmschalten(g, v.id, v.status === 'offen' ? 'erledigt' : 'offen')}>{v.status === 'offen' ? 'offen' : 'erledigt'}</button>
                          : <span className={`ld-gt__status ld-gt__status--${v.status}`}>{v.status}</span>}
                      </div>
                    ))}
                    {g.nachfassen_am && <div className="ld-gt__nachfassen"><MIcon name="event_repeat" size={15} />Nachfassen am {wtTm(g.nachfassen_am)}</div>}
                  </div>
                )}
              </article>
            ))}
          </div>
        ))}
    </section>
  );
}
